/**
 * Turns the policy-extraction model's raw JSON into ParsedPolicyDataExtractResponse: each field
 * keeps what the model returned and gains a typed `parsedFieldValue` (numbers without commas,
 * Yes/No flags, null for "unlimited"/"lifetime" and nullish strings like "N/A").
 *
 * Lives in shared so the client upload flow and the eval package (packages/eval) parse model
 * output with the same code. Post-processing happens next, in postProcessParsedResponse.ts.
 */

export const removeCommas = (value: string) => value.replace(/,/g, '');

export const isNullish = (value: any) =>
  value === null ||
  value === undefined ||
  value === 'None' ||
  value === '' ||
  value === 'null' ||
  value === 'undefined' ||
  value === 'NULL' ||
  value === 'N/A' ||
  value === 'n/a';

export function containsBrackets(str: string): boolean {
  const hasOpeningBracket = str.includes('{');
  const hasClosingBracket = str.includes('}');
  return hasOpeningBracket && hasClosingBracket;
}

function parseFloatWithPotentialKeyTerm(
  fieldValue: string,
  nullKeyTerm: string,
): number | null {
  // used for unlimited or lifetime fields
  return fieldValue.toLowerCase().includes(nullKeyTerm)
    ? null
    : parseFloat(removeCommas(fieldValue));
}

function parseYesNoField(fieldValue: string): 'Yes' | 'No' {
  return fieldValue.toLowerCase() === 'yes' ? 'Yes' : 'No';
}

function parseFieldValue(fieldId: PolicyFieldId, fieldValue: string): any {
  switch (fieldId) {
    case 'policyInflationProtectionType':
    case 'policyPaymentType':
      return fieldValue as PolicyExtractorFieldTypeMap[typeof fieldId];
    case 'isPrimaryPolicyHolder':
    case 'policyHasCobRider':
      return parseYesNoField(fieldValue);
    case 'policyContinuationBenefitAmount':
      return parseFloatWithPotentialKeyTerm(fieldValue, 'unlimited');
    case 'policyMaximumBenefitAmount':
      return parseFloatWithPotentialKeyTerm(fieldValue, 'unlimited');
    case 'policyInflationProtectionYears':
      return parseFloatWithPotentialKeyTerm(fieldValue, 'lifetime');
    default:
      return parseFloat(removeCommas(fieldValue));
  }
}

export function parsePolicyExtractResponse(
  response: string,
): ParsedPolicyDataExtractResponse {
  const responseJson = JSON.parse(response);
  const parsedResponse: Partial<ParsedPolicyDataExtractResponse> = {};

  for (const key in responseJson) {
    if (responseJson.hasOwnProperty(key)) {
      // Remove spaces from the key
      const sanitizedKey = key.replace(/\s+/g, '') as PolicyFieldId;
      const field = responseJson[key];
      if (field) {
        parsedResponse[sanitizedKey] = {
          ...field,
          parsedFieldValue: isNullish(field.fieldValue)
            ? null
            : typeof field.fieldValue === 'string'
              ? parseFieldValue(sanitizedKey, field.fieldValue)
              : field.fieldValue,
        };
      }
    }
  }

  return parsedResponse as ParsedPolicyDataExtractResponse;
}
